import { useEffect, useState } from "react";
import InputField from "../InputField";
import InputDropdown from "../InputDropdown";
import UploadField from "../UploadField";
import ModalParent from "./ModalParent";
import { generalObj } from "../../constant/object_types";
import ButtonFrame from "../ButtonFrame";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { useOutletContext } from "react-router-dom";
import { add_editProduct, fetchCategoryUnitList, fetchStoreListByUserId } from "../../action/generalFunc";
import { toast } from "react-toastify";
import AddUnitModal from "./AddUnitModal";

const AddProductModal = ({
    visible,
    modalClose,
    onAdded,
    initialName = "",
    vendorId,
    storeId,
    vendorName = "",
    storeName = ""
}:{
    visible: boolean,
    modalClose: () => void,
    onAdded: (product: generalObj) => void,
    initialName?: string,
    vendorId?: string | number,
    storeId?: string | number,
    vendorName?: string,
    storeName?: string
}) => {
    const [formData, setFormData] = useState<generalObj>({
        name: initialName,
        size: "",
        unit: null,
        description: "",
        product_image: null,
        image_url: ""
    });
    const [productFile, setProductFile] = useState<File | Blob | null>(null);
    const [unitList, setUnitList] = useState<generalObj[]>([]);
    const [openUnitAdd, setOpenUnitAdd] = useState<boolean>(false);

    const account = useSelector((state: RootState) => state.account);
    const { setModalLoading }: { setModalLoading: Function } = useOutletContext();

    useEffect(() => {
        if (visible) {
            setFormData({
                name: initialName,
                size: "",
                unit: null,
                description: "",
                product_image: null,
                image_url: ""
            });
            setProductFile(null);
        }
    }, [visible, initialName]);

    useEffect(() => {
        if (!account || !account.isAuthenticated || !visible) return;

        const loadUnits = async () => {
            const result = await fetchCategoryUnitList(account.token);
            if (result && result.data?.unit) {
                const list: generalObj[] = result.data.unit.map((element: generalObj) => ({
                    id: element.id,
                    label: element.label,
                    value: element.label
                }));
                setUnitList([
                    ...list,
                    {
                        id: 0,
                        label: "+ Add New Unit",
                        value: "add_unit"
                    }
                ]);
            }
        };

        loadUnits();
    }, [account, visible]);

    const handleUnitChange = (val: generalObj) => {
        if (val.id === 0) {
            setOpenUnitAdd(true);
            return;
        }
        setFormData((prev: generalObj) => ({ ...prev, unit: val }));
    };

    const pickImage = (e: generalObj, isDrag: boolean = false) => {
        const file = !isDrag ? e?.target?.files?.[0] : e;
        if (!file) return;

        if (file.type !== "image/png" && file.type !== "image/jpg" && file.type !== "image/jpeg") {
            toast("Only image files (png, jpg, jpeg) are allowed");
            return;
        }
        if (Math.floor(file.size / 1000) > 300) {
            toast("Image size must not exceed 300kb");
            return;
        }

        setProductFile(file);
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
            if (!reader || !reader.result) return;
            const blob = new Blob([reader.result], { type: file.type || "image/png" });
            setFormData((prev: generalObj) => ({ ...prev, product_image: blob }));
        };
    };

    const clearImage = () => {
        setProductFile(null);
        setFormData((prev: generalObj) => ({ ...prev, product_image: null, image_url: "" }));
    };

    const submitAddProduct = async () => {
        if (!account) return;
        if (!formData.name || formData.name.trim() === "") {
            toast("Product name is required");
            return;
        }

        setModalLoading(true);

        let storeIds: (number | string)[] = [];
        if (vendorId) {
            const storeResult = await fetchStoreListByUserId(account.token, String(vendorId), "vendor");
            const vendorStores = storeResult?.data?.stores || [];
            storeIds = vendorStores.map((s: generalObj) => s.id).filter(Boolean);
        }

        if (storeId && !storeIds.some((sId) => String(sId) === String(storeId))) {
            storeIds.push(Number(storeId) || storeId);
        }

        const apiData: generalObj = {
            name: formData.name.trim(),
            size: formData.size?.trim() || null,
            unit: formData.unit ? formData.unit.id : null,
            vendor: vendorId ? Number(vendorId) || vendorId : null,
            description: formData.description?.trim() || null,
            published: true
        };

        if (productFile) {
            apiData.image = productFile;
        }

        if (storeIds.length > 0) {
            apiData.store = storeIds;
        }

        const result = await add_editProduct(account.token, apiData);
        if (result) {
            toast(result.message || "Product created successfully");
            onAdded(result.data || { name: apiData.name, size: apiData.size, unit: formData.unit });
            modalClose();
        }
        setModalLoading(false);
    };

    return (
        <>
            <ModalParent visible={visible}>
                <div className="ViewContainer">
                    <div className="input__pop" style={{maxWidth:"600px", width:"90vw", maxHeight:"90vh", overflowY:"auto", padding:"24px"}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px"}}>
                            <div>
                                <h4 style={{margin:0, fontSize:"18px"}}>Create New Product</h4>
                                <p style={{margin:"4px 0 0 0", fontSize:"12px", color:"#676967"}}>Creating product on behalf of admin</p>
                            </div>
                            <button type="button" title="Close" onClick={modalClose} style={{fontSize:"24px", background:"transparent", border:"none", cursor:"pointer", padding:"0 8px"}}>&times;</button>
                        </div>

                        {(storeName || vendorName) && (
                            <div style={{backgroundColor:"#F0F5FF", border:"1px solid #D0E1FD", borderRadius:"8px", padding:"10px 14px", marginBottom:"20px", fontSize:"13px", color:"#1159AB"}}>
                                {vendorName && <div><strong>Vendor:</strong> {vendorName}</div>}
                                {storeName && <div><strong>Store:</strong> {storeName}</div>}
                            </div>
                        )}

                        <div>
                            <InputField
                                label="Product Name *"
                                inputProp={{value: formData.name, placeholder: "e.g. Golden Penny Spaghetti"}}
                                onTextChange={(evt) => setFormData((prev: generalObj) => ({ ...prev, name: evt }))}
                                containerStyle={{width:"100%", marginBottom:"18px"}}
                            />
                            <InputField
                                label="Size"
                                inputProp={{value: formData.size, placeholder: "e.g. 500, 1, 6", type:"number", min:"1", step:"1"}}
                                onTextChange={(evt) => setFormData((prev: generalObj) => ({ ...prev, size: evt }))}
                                containerStyle={{width:"100%", marginBottom:"18px"}}
                            />
                            <InputDropdown
                                label="Unit"
                                data={unitList}
                                placeHolder="Select or Add Unit"
                                selectedData={formData.unit}
                                containerStyle={{width:"100%", marginBottom:"18px"}}
                                onItemChange={handleUnitChange}
                            />
                            <InputField
                                label="Description"
                                inputProp={{value: formData.description, placeholder: "Optional product description"}}
                                onTextChange={(evt) => setFormData((prev: generalObj) => ({ ...prev, description: evt }))}
                                containerStyle={{width:"100%", marginBottom:"18px"}}
                            />
                            <UploadField
                                label="Product Image (optional)"
                                current_obj={formData.product_image}
                                current_obj_url={formData.image_url}
                                upload_description="Drag and drop or select an image (Max. 300kb)"
                                file_types="image/png, image/jpg, image/jpeg"
                                containerStyle={{width:"100%", marginBottom:"18px"}}
                                inputContainerStyle={{height:"130px"}}
                                onSelectFile={pickImage}
                                onInputClear={clearImage}
                            />
                        </div>

                        <div style={{display:"flex", justifyContent:"flex-end", alignItems:"center", gap:"14px", marginTop:"32px", flexWrap:"wrap"}}>
                            <ButtonFrame
                                btn_text="Cancel"
                                btn_style={{padding:"10px 20px", borderRadius:"8px", border:"1px solid #808280", color:"#808280", fontSize:"14px", fontWeight:"600"}}
                                onPress={modalClose}
                            />
                            <ButtonFrame
                                btn_text="Create Product"
                                btn_style={{padding:"10px 20px", borderRadius:"8px", backgroundColor:"#00983A", color:"#FFF", fontSize:"14px", fontWeight:"600"}}
                                onPress={submitAddProduct}
                            />
                        </div>
                    </div>
                </div>
            </ModalParent>

            <AddUnitModal
                visible={openUnitAdd}
                modalClose={() => setOpenUnitAdd(false)}
                onAdded={(newUnit) => {
                    const addedUnit = { id: newUnit.id, label: newUnit.label, value: newUnit.label };
                    setUnitList((prev) => [addedUnit, ...prev]);
                    setFormData((prev: generalObj) => ({ ...prev, unit: addedUnit }));
                    setOpenUnitAdd(false);
                }}
            />
        </>
    );
};

export default AddProductModal;
